import { noop, findIndex } from 'underscore';
import React, { useCallback, useContext, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Button } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { getUrlWithProtocol } from '@bingads-webui-campaign-react/url-utils';
import { useIntersectionObserver, getIsEmailMeEnabled } from '@bingads-webui-campaign-react/pmax-landing-utils';
import {
  AutoPlayAdPreviews,
  AdsDataMapping,
  EdgeChannelIcon,
  SearchChannelIcon,
  OutlookChannelIcon,
  MSNChannelIcon,
} from '@bingads-webui-campaign-react/pmax-landing-ad-preview';
import {
  UAPPlacementTypeAlias as EPT,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-core';

import { WebsitePromptInputs, ErrorMessageBanner, EmailMeDialog } from '../shared';
import { getAdPlacementList } from './format-data';
import { getPreviewResultStyles } from '../styles/preview-result-styles';

export function PreviewResult({
  triggerTransition,
  promptSuggestions,
  adSuggestions,
  onClickSignUp,
  website,
  setWebsite,
  prompt,
  setPrompt,
  signUpUrlWithCacheId,
  errorMessage,
  logger,
}) {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getPreviewResultStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const isEmailMeEnabled = getIsEmailMeEnabled();

  const {
    imageUrl,
    adData,
  } = adSuggestions || {};

  const [customPlacementIndex, setCustomPlacementIndex] = useState();
  const [isValid, setIsValid] = useState(true);
  const [alwaysShowError, setAlwaysShowError] = useState(false);
  const adPreviewRef = useRef();

  const onRefreshClick = useCallback(() => {
    if (isValid) {
      logger.trace(`[PreviewResult] Refresh clicked to submit website and prompt. Website: ${website}. Prompt: ${prompt}`);
      triggerTransition();
    } else {
      logger.trace(`[PreviewResult] Refresh clicked with invalid website or prompt. Website: ${website}. Prompt: ${prompt}`);
      setAlwaysShowError(true);
    }
  }, [isValid, logger, prompt, triggerTransition, website]);

  const adPlacementList = useMemo(() => getAdPlacementList(imageUrl, errorMessage), [imageUrl, errorMessage]);

  const getAdDataForPlacement = useCallback((placementType) => {
    if (!adData) {
      return AdsDataMapping[placementType];
    }
    return {
      ...adData,
      displayUrl: website,
      finalUrl: getUrlWithProtocol(website),
    };
  }, [adData, website]);

  const isAdPreviewVisible = useIntersectionObserver({
    ref: adPreviewRef,
  });

  const onCurrentPlacementChange = useCallback((index, placementType) => {
    if (isAdPreviewVisible) {
      logger.trace(`[AdPreview] Placement ${index} (${placementType}) active`);
    }
  }, [isAdPreviewVisible, logger]);

  const getOnClickChannelItem = useCallback(placementType => (() => {
    const index = findIndex(adPlacementList, p => p.placementMeta.placementType === placementType);
    if (index !== -1) {
      setCustomPlacementIndex(index);
    }
  }), [adPlacementList]);

  return (
    <div className={classNames('preview-result', classes.resultContainer, classes[`resultContainer-${displayMode}`])}>
      <div className="result-title-container">
        <h1 className="result-title">
          {i18n.getString(_TL_('We created a set of ads for your business and your goal'))}
        </h1>
      </div>
      <div className={classNames('preview-inputs', classes.inputsContainer, classes[`inputsContainer-${displayMode}`])}>
        <WebsitePromptInputs
          website={website}
          setWebsite={setWebsite}
          prompt={prompt}
          setPrompt={setPrompt}
          setIsValid={setIsValid}
          alwaysShowError={alwaysShowError}
          showPrompt
          promptSuggestions={promptSuggestions}
          onRefreshClick={onRefreshClick}
          logger={logger}
        />
      </div>
      <div className={classNames('ad-suggestions', classes.suggestionsContainer, classes[`suggestionsContainer-${displayMode}`])}>
        <div className={classNames('intro-preview', classes.introContainer, classes[`introContainer-${displayMode}`])}>
          <h2 className="intro-title">{i18n.getString(_TL_('Distribute your ads across Microsoft Audience Networks'))}</h2>
          <p className="intro-description">
            {i18n.getString(_TL_('Exceed your growth potential and reach billions of people online. Leverage Microsoft\'s new Bing and Edge, driven by the power of generative artificial intelligence (AI).'))}
          </p>
          <div className="channel-title">
            {i18n.getString(_TL_('Distributing channels'))}
          </div>
          <div className="channel-list">
            <span
              className="edge-channel-item"
              role="button"
              tabIndex={0}
              onClick={getOnClickChannelItem(EPT.Responsive.MSAN.MSEdgeSingleCarousel)}
              onKeyDown={getOnClickChannelItem(0)}
              aria-label={i18n.getString(_TL_('Edge'))}
            >
              <EdgeChannelIcon />
            </span>
            <span
              className="search-channel-item"
              role="button"
              tabIndex={0}
              onClick={getOnClickChannelItem(EPT.Responsive.BingSearch.Text)}
              onKeyDown={getOnClickChannelItem(1)}
              aria-label={i18n.getString(_TL_('Bing search'))}
            >
              <SearchChannelIcon />
            </span>
            <span
              className="msn-channel-item"
              role="button"
              tabIndex={0}
              onClick={getOnClickChannelItem(EPT.Responsive.MMA.LandscapeImage)}
              onKeyDown={getOnClickChannelItem(2)}
              aria-label={i18n.getString(_TL_('MSN'))}
            >
              <MSNChannelIcon />
            </span>
            <span
              className="outlook-channel-item"
              role="button"
              tabIndex={0}
              onClick={getOnClickChannelItem(EPT.Responsive.Outlook.Mobile)}
              onKeyDown={getOnClickChannelItem(3)}
              aria-label={i18n.getString(_TL_('Outlook'))}
            >
              <OutlookChannelIcon />
            </span>
          </div>
        </div>
        <div className={classNames('ad-preview', classes.adPreviewContainer, classes[`adPreviewContainer-${displayMode}`])}>
          <AutoPlayAdPreviews
            ref={adPreviewRef}
            adPlacementList={adPlacementList}
            getAdDataForPlacement={getAdDataForPlacement}
            onCurrentPlacementChange={onCurrentPlacementChange}
            customPlacementIndex={customPlacementIndex}
          />
        </div>
      </div>
      {errorMessage && <ErrorMessageBanner errorMessage={errorMessage} />}
      <div className={classNames('button-container', classes.buttonContainer, classes[`buttonContainer-${displayMode}`])}>
        <Button
          className="create-campaign-button"
          onClick={onClickSignUp}
          appearance="primary"
        >
          {i18n.getString(_TL_('Get started'))}
        </Button>
        {isEmailMeEnabled && (
          <EmailMeDialog
            signUpUrlWithCacheId={signUpUrlWithCacheId}
            website={website}
            logger={logger}
          />
        )}
      </div>
    </div>
  );
}

PreviewResult.propTypes = {
  triggerTransition: PropTypes.func.isRequired,
  promptSuggestions: PropTypes.arrayOf(PropTypes.string),
  adSuggestions: PropTypes.shape({
    imageUrl: PropTypes.string,
    adData: PropTypes.shape({}),
  }),
  onClickSignUp: PropTypes.func.isRequired,
  website: PropTypes.string.isRequired,
  setWebsite: PropTypes.func.isRequired,
  prompt: PropTypes.string.isRequired,
  setPrompt: PropTypes.func.isRequired,
  signUpUrlWithCacheId: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

PreviewResult.defaultProps = {
  adSuggestions: {},
  promptSuggestions: [],
  errorMessage: '',
  logger: {
    trace: noop,
  },
};
